import { Controller } from "stimulus";

const SPEEDS = [1, 1.25, 1.5, 1.75, 2];

export default class extends Controller {
  static targets = [
    "audio",
    "playButton",
    "pauseButton",
    "elapsed",
    "duration",
    "progressTrack",
    "progressFill",
    "volumeIcon",
    "volumeSlider",
    "speedButton"
  ];

  initialize() {
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onDurationAvailable = this.onDurationAvailable.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.speedIndex = 0;
    this.showHours = false;
  }

  connect() {
    this.audioTarget.addEventListener("timeupdate", this.onTimeUpdate);
    this.audioTarget.addEventListener("loadedmetadata", this.onDurationAvailable);
    this.audioTarget.addEventListener("durationchange", this.onDurationAvailable);
    this.audioTarget.addEventListener("ended", this.onEnded);

    if (this.hasDuration()) this.onDurationAvailable();
  }

  disconnect() {
    this.audioTarget.removeEventListener("timeupdate", this.onTimeUpdate);
    this.audioTarget.removeEventListener("loadedmetadata", this.onDurationAvailable);
    this.audioTarget.removeEventListener("durationchange", this.onDurationAvailable);
    this.audioTarget.removeEventListener("ended", this.onEnded);
  }

  // Actions

  async play() {
    try {
      await this.audioTarget.play();
      this.showPlaying();
    } catch (error) {
      this.showPaused();
    }
  }

  pause() {
    this.audioTarget.pause();
    this.showPaused();
  }

  seek(event) {
    if (!this.hasDuration()) return;

    const rect = this.progressTrackTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    this.audioTarget.currentTime = ratio * this.audioTarget.duration;
  }

  changeSpeed() {
    this.speedIndex = (this.speedIndex + 1) % SPEEDS.length;
    const speed = SPEEDS[this.speedIndex];
    this.audioTarget.playbackRate = speed;
    this.speedButtonTarget.textContent = `${speed}x`;
  }

  changeVolume() {
    const volume = this.volumeSliderTarget.value / 100;
    this.audioTarget.volume = volume;
    this.renderVolumeIcon(volume);
  }

  // Audio event handlers

  onDurationAvailable() {
    if (!this.hasDuration()) return;

    this.showHours = this.audioTarget.duration >= 3600;
    this.durationTarget.textContent = this.formatTime(this.audioTarget.duration);
    this.elapsedTarget.textContent = this.formatTime(0);
  }

  onTimeUpdate() {
    const current = this.audioTarget.currentTime;
    const duration = this.audioTarget.duration;

    this.elapsedTarget.textContent = this.formatTime(current);

    if (this.hasDuration()) {
      this.progressFillTarget.style.width = `${(current / duration) * 100}%`;
    }
  }

  onEnded() {
    this.showPaused();
    this.progressFillTarget.style.width = "0%";
    this.elapsedTarget.textContent = this.formatTime(0);
    this.audioTarget.currentTime = 0;
  }

  // Private

  showPlaying() {
    this.toggle(this.playButtonTarget, false);
    this.toggle(this.pauseButtonTarget, true);
  }

  showPaused() {
    this.toggle(this.playButtonTarget, true);
    this.toggle(this.pauseButtonTarget, false);
  }

  renderVolumeIcon(volume) {
    const paths = this.volumeIconTarget.querySelectorAll("path");
    const wavePath = paths[1];
    if (!wavePath) return;

    wavePath.style.display = volume === 0 ? "none" : "";
  }

  hasDuration() {
    const d = this.audioTarget.duration;
    return d && isFinite(d) && d > 0;
  }

  formatTime(seconds) {
    const total = Math.floor(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;

    if (this.showHours) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  toggle(element, show) {
    element.classList.toggle("hidden", !show);
  }
}
